import { CurrencyHelpers } from 'helpers/currencyHelpers';
import { RouteHelpers } from 'helpers/routerHelpers';
import { useSearchParams } from 'next/navigation';
import { Product } from 'shared/types/product';

interface Props {
  product: Product;
}
export default function ProductGroupJsonSchema({ product }: Props) {
  const searchParams = useSearchParams();
  const hasColorQueryParam = searchParams.get('color');
  const hasSizeQueryParam = searchParams.get('size');

  if (hasColorQueryParam || hasSizeQueryParam) return null;
  return (
    <script
      type="application/ld+json"
      id="product-group-schema"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          '@context': 'https://schema.org/',
          '@type': 'ProductGroup',
          name: product.name,
          image: [product.variants[0]?.images],
          description: product.description,
          sku: product.variants[0]?.sku?.replace(/[^a-zA-Z0-9]/g, ''),
          brand: {
            '@type': 'Brand',
            name: 'Shades of Light',
          },
          hasVariant: product.variants.map((variant) => {
            const hasSize = variant.attributes?.size;
            const hasMultipleColors = product.variants.length > 1;

            const queryParams =
              hasSize || hasMultipleColors
                ? RouteHelpers.encodeToURL({
                    size: variant?.attributes?.size,
                    color: variant.attributes?.actualColor,
                  })
                : '';

            const originalPrice = variant?.originalPrice ? CurrencyHelpers.formatAmount(variant.originalPrice) : null;
            const discountedPrice: number = variant.discountedPrice
              ? CurrencyHelpers.formatAmount(variant.discountedPrice)
              : null;

            const isOnSale = originalPrice || discountedPrice;

            return {
              '@type': 'Product',
              name: `${product.name} - ${variant.attributes?.actualColor}`,
              sku: variant.sku.replace(/[^a-zA-Z0-9]/g, ''),
              color: variant.attributes?.actualColor,
              size: variant.attributes?.size || variant.attributes?.actualSize,
              offers: {
                '@type': 'Offer',
                url: `https://shadesoflight.com/products/${product._url}${queryParams}`,
                priceCurrency: 'USD',
                price: CurrencyHelpers.formatAmount(
                  variant.price || { centAmount: 0, currencyCode: 'USD', fractionDigits: 2 },
                ),
                itemCondition: 'https://schema.org/NewCondition',
                availability: 'https://schema.org/InStock',
                hasMerchantReturnPolicy: {
                  '@context': 'http://schema.org/',
                  '@type': 'MerchantReturnPolicy',
                  '@id': '#return_policy',
                  applicableCountry: 'US',
                  returnPolicyCountry: 'US',
                  returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
                  merchantReturnDays: isOnSale ? 30 : 60,
                  returnMethod: 'https://schema.org/ReturnByMail',
                  returnFees: 'https://schema.org/FreeReturn',
                },
              },
            };
          }),
        }),
      }}
    />
  );
}
